import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Mail,
  Phone,
  MapPin,
  Clock,
  Send,
  Loader2,
  CheckCircle,
  Instagram,
} from 'lucide-react';
import { leadsAPI } from '../lib/api';
import { toast } from 'sonner';
import { LP_URL, WHATSAPP_URL } from '../lib/utils';
import { usePageMeta } from '../components/shared/SEO';

const ContactPage = () => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    whatsapp: '',
    clinic_name: '',
    message: '',
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await leadsAPI.create({
        ...formData,
        source: 'contact-page',
      });
      
      setSubmitted(true);
      toast.success('Mensagem enviada com sucesso!');
      
      // Redirect to LP after delay
      setTimeout(() => {
        window.open(LP_URL, '_blank');
      }, 2000);
    } catch (error) {
      toast.error('Erro ao enviar. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  usePageMeta({
    title: 'Contato | Fale com Especialistas em Contabilidade Médica - Apex Medical Hub',
    description: 'Entre em contato com a Apex Medical Hub. Sediados em Boa Vista/RR, atendemos clínicas e consultórios médicos em todo o Brasil via WhatsApp, email e videochamada.',
    canonical: '/contato',
  });

  return (
    <div data-testid="contact-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-50 to-white pt-28 pb-10">
        <div className="container-apex">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-slate-900 mb-6">
              Entre em Contato
            </h1>
            <p className="text-slate-600 text-lg">
              Estamos prontos para ajudar sua clínica a crescer com mais organização, eficiência e segurança. Fale conosco.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="section-padding">
        <div className="container-apex">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div>
              {submitted ? (
                <div className="bg-white rounded-xl shadow-lg p-8 text-center" data-testid="contact-success">
                  <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                  <h2 className="font-heading text-2xl font-bold text-slate-900 mb-2">
                    Mensagem enviada!
                  </h2>
                  <p className="text-slate-600 mb-6">
                    Obrigado pelo contato. Nossa equipe responderá em breve.
                  </p>
                  <Button
                    asChild
                    className="btn-primary"
                  >
                    <a href={LP_URL} target="_blank" rel="noopener noreferrer">
                      Falar agora pelo WhatsApp
                    </a>
                  </Button>
                </div>
              ) : (
                <div className="bg-white rounded-xl shadow-lg p-8" data-testid="contact-form">
                  <h2 className="font-heading text-2xl font-bold text-slate-900 mb-6">
                    Envie sua mensagem
                  </h2>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="name">Nome *</Label>
                        <Input
                          id="name"
                          name="name"
                          type="text"
                          placeholder="Seu nome completo"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          className="mt-1"
                          data-testid="contact-name"
                        />
                      </div>
                      <div>
                        <Label htmlFor="email">E-mail *</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          placeholder="seu@email.com"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="mt-1"
                          data-testid="contact-email"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="whatsapp">WhatsApp</Label>
                        <Input
                          id="whatsapp"
                          name="whatsapp"
                          type="tel"
                          placeholder="(00) 00000-0000"
                          value={formData.whatsapp}
                          onChange={handleChange}
                          className="mt-1"
                          data-testid="contact-whatsapp"
                        />
                      </div>
                      <div>
                        <Label htmlFor="clinic_name">Nome da clínica/consultório</Label>
                        <Input
                          id="clinic_name"
                          name="clinic_name"
                          type="text"
                          placeholder="Nome da sua clínica"
                          value={formData.clinic_name}
                          onChange={handleChange}
                          className="mt-1"
                          data-testid="contact-clinic"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="message">Mensagem</Label>
                      <Textarea
                        id="message"
                        name="message"
                        placeholder="Como podemos ajudar?"
                        value={formData.message}
                        onChange={handleChange}
                        rows={5}
                        className="mt-1"
                        data-testid="contact-message"
                      />
                    </div>

                    <Button
                      type="submit"
                      className="btn-primary w-full py-6"
                      disabled={loading}
                      data-testid="contact-submit"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          Enviando...
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          Enviar mensagem
                        </>
                      )}
                    </Button>
                  </form>
                </div>
              )}
            </div>

            {/* Contact Info */}
            <div className="space-y-8">
              <div>
                <h2 className="font-heading text-2xl font-bold text-slate-900 mb-6">
                  Informações de contato
                </h2>
                <div className="space-y-6">
                  <a
                    href={WHATSAPP_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-start gap-4 p-4 bg-white rounded-xl border border-slate-100 hover:shadow-md transition-all"
                  >
                    <div className="w-12 h-12 rounded-xl bg-[#25D366]/10 flex items-center justify-center flex-shrink-0">
                      <Phone className="w-6 h-6 text-[#25D366]" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900">WhatsApp</h4>
                      <p className="text-slate-600">(95) 99154-8020</p>
                      <p className="text-sm text-[#007f73]">Clique para conversar</p>
                    </div>
                  </a>

                  <a
                    href="mailto:contato@apexmedicalhub.com"
                    className="flex items-start gap-4 p-4 bg-white rounded-xl border border-slate-100 hover:shadow-md transition-all"
                  >
                    <div className="w-12 h-12 rounded-xl bg-[#1e4e7e]/10 flex items-center justify-center flex-shrink-0">
                      <Mail className="w-6 h-6 text-[#1e4e7e]" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900">E-mail</h4>
                      <p className="text-slate-600">contato@apexmedicalhub.com</p>
                    </div>
                  </a>

                  <div className="flex items-start gap-4 p-4 bg-white rounded-xl border border-slate-100">
                    <div className="w-12 h-12 rounded-xl bg-[#007f73]/10 flex items-center justify-center flex-shrink-0">
                      <Clock className="w-6 h-6 text-[#007f73]" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900">Horário de atendimento</h4>
                      <p className="text-slate-600">Segunda a Sexta: 8h às 18h</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 p-4 bg-white rounded-xl border border-slate-100">
                    <div className="w-12 h-12 rounded-xl bg-[#1e4e7e]/10 flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-[#1e4e7e]" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900">Localização</h4>
                      <p className="text-slate-600">Sediados em Boa Vista, Roraima</p>
                      <p className="text-sm text-[#007f73]">Atendemos clínicas em todo o Brasil</p>
                    </div>
                  </div>

                  <a
                    href="https://www.instagram.com/apexmedicalhub/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-start gap-4 p-4 bg-white rounded-xl border border-slate-100 hover:shadow-md transition-all"
                    data-testid="contact-instagram-link"
                  >
                    <div className="w-12 h-12 rounded-xl bg-[#E4405F]/10 flex items-center justify-center flex-shrink-0">
                      <Instagram className="w-6 h-6 text-[#E4405F]" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900">Instagram</h4>
                      <p className="text-slate-600">@apexmedicalhub</p>
                      <p className="text-sm text-[#007f73]">Siga-nos no Instagram</p>
                    </div>
                  </a>
                </div>
              </div>

              {/* CTA Card */}
              <div className="bg-gradient-to-br from-[#1e4e7e] to-[#007f73] rounded-xl p-8 text-white">
                <h3 className="font-heading text-2xl font-bold mb-4">
                  Prefere uma conversa direta?
                </h3>
                <p className="text-white/80 mb-6">
                  Agende uma análise gratuita da sua operação e descubra como podemos ajudar sua clínica.
                </p>
                <Button
                  asChild
                  className="w-full bg-white text-[#1e4e7e] hover:bg-white/90"
                >
                  <a href={LP_URL} target="_blank" rel="noopener noreferrer">
                    Agendar conversa
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
